import React, { useState, useEffect, useRef } from 'react';
import './my-invoices.scss';

import {
  Form,
  Button,
  ButtonColor,
  ButtonVariant,
  ButtonType,
  DataTable,
  TableVariant,
  FormValidationMode,
} from '@ampol/design-system';
import { getInvoicesAction, resetInvoicesAction, getInvoiceDocumentDownload } from '../../actions/accountActions';
import { useSelector, useDispatch } from 'react-redux';
import { fileSizeInMB, normaliseArray } from '../../utilities/core-utilities';
import { formatDate, yupDateTransformer } from '../../utilities/dayjs-utilities';
import { DATE_FORMAT, DATE_FORMATS, DATE_FORMAT_HYPHEN } from 'utilities/constants';
import DeprecatedIcon from '../../assets/svg-icons';
import * as yup from 'yup';
import ContentControls from '../../components/content-controls/content-controls';
import FilterControl from '../../components/filter-control/filter-control';
import PageHeader from '../../components/layout/page-header/page-header';
import dayjs from 'dayjs';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';
import { UserState } from 'types/UserState';

const InvoicesOverview = () => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);

  const userStore: UserState = useSelector((state: any) => state.userStore);
  const assignedGrandfatherClientNumber = userStore.assignedGrandfatherClientNumber;
  const assignedAccountNumber = userStore.assignedAccountNumber;
  const invoices = useSelector((state: any) => state.accountsStore.invoices);
  const totalInvoiceStatementRecords = useSelector((state: any) => state.accountsStore.totalInvoiceStatementRecords);
  const invoicesLoading = useSelector((state: any) => state.accountsStore.isInvoicesLoading);
  const allAccounts = useSelector((state: any) => state.accountsStore.allAccounts);
  const portalType = userStore.portalType;
  const loggedInUser = userStore.loggedInUser;

  const reportFromDate = dayjs().subtract(1, 'year').format(DATE_FORMAT);
  const reportToDate = dayjs().format(DATE_FORMAT);

  const [filterBarEnabled, setFilterBarEnabled] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);

  const [filterValues, setFilterValues] = useState({
    accountNumber: null,
    from: reportFromDate,
    to: reportToDate,
    pageSize: 10,
    pageNumber: 1,
    sortField: 'publishedDate',
    sortDirection: 'desc',
    type: 'All',
  });

  const invoiceTypesMap = {
    card: [
      'All',
      'Statement Invoice',
      'Customer Statement',
      'Period Rebate Invoice',
      'Trading Inv Summary',
      'ITD Summary Report',
      'Trading Inv Veh Perform',
      'ITD Transactions Report',
      'Trading Inv Detail',
      'AmpolCard Invoice',
    ],
    cash: ['All', 'Customer Statement', 'AmpolCash Invoice', 'AmpolCash Load RCTI'],
  };

  const invoiceTypes = normaliseArray(invoiceTypesMap[portalType]) || [];

  const filterFormSchema = yup.object().shape({
    type: yup.string(),
    from: yup.date().nullable().required(Validation.INVOICE_DATE_FROM_REQUIRED).transform(yupDateTransformer),
    to: yup
      .date()
      .nullable()
      .transform(yupDateTransformer)
      .required(Validation.INVOICE_DATE_TO_REQUIRED)
      .min(yup.ref('from'), Validation.INVOICE_DATE_TO_AFTER_FROM),
  });

  useEffect(() => {
    dispatch(resetInvoicesAction());

    if (isMounted.current) {
      // So this does not conflict with handleAccountSelected when allAccounts.length === 1
      setFilterValues({
        accountNumber: null,
        from: reportFromDate,
        to: reportToDate,
        pageSize: 10,
        pageNumber: 1,
        sortField: 'publishedDate',
        sortDirection: 'desc',
        type: 'All',
      });

      setFilterApplied(false);
      setFilterBarEnabled(false);
    }

    isMounted.current = true;
  }, [assignedAccountNumber]);

  useEffect(() => {
    callDispatchGetInvoices();
  }, [filterValues]);

  const toggleFilter = () => {
    setFilterBarEnabled(!filterBarEnabled);
  };

  const handleDownloadInvoice = (grandParentAccountNumber, contractIdentifier, documentId, fileType, invoiceNumber) => {
    grandParentAccountNumber.startsWith('05')
      ? window.dataLayer.push({
          event: 'CP_Reports_Download_Customer_Statement',
          button_click: true,
        })
      : window.dataLayer.push({
          event: 'Card_Portal_Reports_Download_Invoice_Statement',
          button_click: true,
        });
    trackCustomEvent(
      'Click Download',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Download icon to download Invoice.',
      { invoiceNumber, selectedAccount: contractIdentifier }
    );
    dispatch(getInvoiceDocumentDownload(grandParentAccountNumber, contractIdentifier, documentId, fileType));
  };

  const handlePageChange = (pageNumber) => {
    setFilterValues({
      ...filterValues,
      pageNumber: pageNumber,
    });

    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of Invoices and Statements data.'
    );
  };

  const callDispatchGetInvoices = () => {
    if (filterValues?.accountNumber && filterValues?.from && filterValues?.to) {
      dispatch(
        getInvoicesAction(
          assignedGrandfatherClientNumber,
          filterValues?.accountNumber,
          dayjs(filterValues?.from, DATE_FORMATS).format(DATE_FORMAT_HYPHEN),
          dayjs(filterValues?.to, DATE_FORMATS).format(DATE_FORMAT_HYPHEN),
          filterValues?.pageSize,
          filterValues?.pageNumber,
          filterValues?.sortField,
          filterValues?.sortDirection,
          filterValues?.type === 'All' ? '' : filterValues.type
        )
      );
    }
  };

  const handleAccountSelected = (selectedAccountNumber) => {
    trackCustomEvent(
      'Select Account Dropdown',
      loggedInUser,
      assignedAccountNumber,
      'Selected an Account to view related Invoices and Statements',
      { selectedAccount: selectedAccountNumber }
    );
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      accountNumber: selectedAccountNumber,
    });
  };

  const handleFormSubmit = (form) => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Filter_Invoice_Statement',
      button_click: true,
    });

    trackCustomEvent(
      'Submit Filter Invoices and Statements',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Filter button to filter Invoices and Statements List based on Invoice Type and Date Range.',
      { selectedAccount: filterValues?.accountNumber }
    );
    const isFilterDefault =
      form.type === 'All' &&
      dayjs(form.from).isSame(dayjs(reportFromDate, DATE_FORMATS)) &&
      dayjs(form.to).isSame(dayjs(reportToDate, DATE_FORMATS));

    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      from: form.from ? dayjs(form.from).format(DATE_FORMAT) : undefined,
      to: form.to ? dayjs(form.to).format(DATE_FORMAT) : undefined,
      type: form.type,
    });

    setFilterBarEnabled(false);
    setFilterApplied(!isFilterDefault);
  };

  const handleTableColumnSorted = (column, isAsc) => {
    setFilterValues({
      ...filterValues,
      sortField: column,
      sortDirection: isAsc ? 'asc' : 'desc',
    });
  };

  const handleClearFilters = () => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Clear_All_Invoice_Statement',
      button_click: true,
    });
    setFilterValues({
      ...filterValues,
      from: null,
      to: null,
      type: 'All',
    });
  };

  return (
    <>
      <PageHeader
        showAccountSelector={true}
        accounts={allAccounts}
        isAccountsFlatList={true}
        onAccountSelected={handleAccountSelected}
        selectedAccountNumber={filterValues?.accountNumber}
      />
      <FilterControl
        showFilterControl={filterValues?.accountNumber !== undefined}
        filterToggle={toggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
      >
        <div className='invoices__filter-form'>
          <Form
            schema={filterFormSchema}
            onSubmit={handleFormSubmit}
            defaultValues={filterValues}
            validationMode={FormValidationMode.onChange}
          >
            <div className='invoices__filter-params'>
              <div className='invoices__filter-params__subtitle0'></div>
              <div className='invoices__filter-input-container'>
                <Form.DropdownSearch label='Invoice type' name='type' data={invoiceTypes} allowClear={false} />
              </div>
              <div className='invoices__filter-params__subtitle1'>Invoice date (range)</div>
              <div className='invoices__span-2'>
                <div className='invoices__filter-date'>
                  <Form.DatePicker
                    placeholder='dd/mm/yyyy'
                    name='from'
                    label='Invoice date from'
                    ariaLabel='Invoice Date From'
                    inputSuffix={
                      <SuffixWrapper>
                        <DeprecatedIcon iconName='calender' />
                      </SuffixWrapper>
                    }
                  />
                </div>
                <div className='invoices__filter-input-joiner' />
                <div className='invoices__filter-date'>
                  <Form.DatePicker
                    placeholder='dd/mm/yyyy'
                    name='to'
                    label='Invoice date to'
                    labelType='hidden'
                    inputSuffix={
                      <SuffixWrapper>
                        <DeprecatedIcon iconName='calender' />
                      </SuffixWrapper>
                    }
                  />
                </div>
              </div>
              <div className='invoices__filter-button-container'>
                <Button
                  variant={ButtonVariant.Secondary}
                  color={ButtonColor.Blue}
                  label='Clear All'
                  type={ButtonType.Reset}
                  onClick={handleClearFilters}
                />
                <Button
                  variant={ButtonVariant.Primary}
                  color={ButtonColor.Blue}
                  label='Filter'
                  type={ButtonType.Submit}
                />
              </div>
            </div>
          </Form>
        </div>
      </FilterControl>
      <ContentControls
        showPageControls={totalInvoiceStatementRecords > filterValues.pageSize}
        showFilterControl={false}
        filterToggle={toggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
        totalRecords={totalInvoiceStatementRecords ? totalInvoiceStatementRecords : 0}
        pageNumber={filterValues.pageNumber}
        itemCount={totalInvoiceStatementRecords}
        onPageChange={handlePageChange}
      />
      <div className='invoices__table'>
        <DataTable
          caption={'Invoices and Statements'}
          columns={[
            {
              header: 'Invoice Date',
              id: 'publishedDate',
              accessorKey: '0',
              defaultSorted: 'desc',
            },
            {
              header: 'Invoice Type',
              id: 'reportName',
              accessorKey: '1',
            },
            {
              header: 'Invoice Number',
              id: 'invoiceNumber',
              accessorKey: '2',
            },
            {
              header: 'File Size',
              id: 'fileSize',
              accessorKey: '3',
              rtl: true,
            },
            {
              header: 'Format',
              id: 'contentType',
              accessorKey: '4',
              disableSort: true,
            },
            {
              id: 'action',
              accessorKey: '5',
              rtl: true,
            },
          ]}
          tableVariant={TableVariant.List}
          onColumnSorted={handleTableColumnSorted}
          rows={invoices?.map((invoice, index) => {
            return [
              formatDate(invoice.publishedDate, DATE_FORMAT),
              invoice.reportName,
              invoice.invoiceNumber,
              fileSizeInMB(invoice.fileSize),
              invoice.contentType,
              <a
                key={`download-${index}`}
                href='#'
                onClick={() =>
                  handleDownloadInvoice(
                    assignedGrandfatherClientNumber,
                    filterValues.accountNumber,
                    invoice.documentId,
                    invoice.contentType?.toLowerCase(),
                    invoice.invoiceNumber
                  )
                }
              >
                <svg width='20' height='20'>
                  <DeprecatedIcon iconName='ampol-download' />
                </svg>
              </a>,
            ];
          })}
          isLoading={invoicesLoading}
          noDataMessage={
            filterValues.accountNumber
              ? 'No invoices for selected account.'
              : 'Select account to view invoices for that account.'
          }
        />
      </div>
    </>
  );
};

export default InvoicesOverview;
